import React from 'react';
import { getAIApiEndpoint } from '../../utils';

// Utility function to simplify data for AI requests
// This will truncate large arrays and simplify nested objects
// to avoid sending too much data to the AI service
// You can adjust the maxArrayLength to control how much data is sent
// Default is set to 50 items per array
const simplifyData = (data: any, maxArrayLength: number = 50): any => {
    if (Array.isArray(data) && data.length > maxArrayLength) {
        return data.slice(0, maxArrayLength);
    } else if (typeof data === 'object' && data !== null) {
        const simplified: any = {};
        for (const key in data) {
            if (Array.isArray(data[key]) && data[key].length > maxArrayLength) {
                simplified[key] = data[key].slice(0, maxArrayLength);
            } else {
                simplified[key] = simplifyData(data[key], maxArrayLength);
            }
        }
        return simplified;
    }
    return data;
};

export const createAIDialogButton = (
    contentRef: React.RefObject<HTMLDivElement>,
    apiEndpoint: string = getAIApiEndpoint(),
    authorizationToken?: string,
    data?: any
): HTMLDivElement => {
    const container = document.createElement('div');
    container.className = 'rich-text-ai-dialog-container';
    container.style.cssText = `
        position: relative;
        display: inline-block;
    `;

    const button = document.createElement('button');
    button.innerHTML = 'AI';
    button.title = 'Generate with AI';
    button.style.cssText = `
        padding: 6px 10px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
    `;

    // Dialog
    const dialog = document.createElement('div');
    dialog.className = 'rich-text-ai-dialog';
    dialog.style.cssText = `
        position: fixed;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        padding: 16px;
        min-width: 320px;
        max-width: 400px;
        z-index: 10001;
        display: none;
    `;

    // Position dialog function
    const positionDialog = () => {
        const buttonRect = button.getBoundingClientRect();
        const dialogWidth = 320;
        const dialogHeight = 150; // Approximate height
        const padding = 10;

        let left = buttonRect.left;
        let top = buttonRect.bottom + padding;

        // Check if dialog would go off the right edge
        if (left + dialogWidth > window.innerWidth - padding) {
            left = window.innerWidth - dialogWidth - padding;
        }

        // Check if dialog would go off the left edge
        if (left < padding) {
            left = padding;
        }

        // Check if dialog would go off the bottom edge
        if (top + dialogHeight > window.innerHeight - padding) {
            top = buttonRect.top - dialogHeight - padding;
        }

        // Check if dialog would go off the top edge
        if (top < padding) {
            top = padding;
        }

        dialog.style.left = `${left}px`;
        dialog.style.top = `${top}px`;
    };

    // Message area
    const messageArea = document.createElement('div');
    messageArea.style.cssText = `
        min-height: 32px;
        margin-bottom: 8px;
        color: #333;
        font-size: 13px;
    `;
    dialog.appendChild(messageArea);

    // Text box
    const input = document.createElement('textarea');
    input.placeholder = 'Type instructions for AI...';
    input.style.cssText = `
        width: 100%;
        min-height: 48px;
        margin-bottom: 8px;
        font-size: 14px;
        border-radius: 4px;
        border: 1px solid #ccc;
        padding: 6px;
        resize: vertical;
        box-sizing: border-box;
        outline: none;
        font-family: inherit;
    `;
    dialog.appendChild(input);

    // Add keyboard event handling for input
    input.addEventListener('keydown', (e) => {
        e.stopPropagation(); // Prevent event from bubbling up to toolbar

        // Submit on Ctrl/Cmd + Enter
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            submitBtn.click();
        }

        // Close on Escape
        if (e.key === 'Escape') {
            e.preventDefault();
            closeDialog();
        }
    });

    // Prevent input events from propagating
    input.addEventListener('input', (e) => {
        e.stopPropagation();
    });

    input.addEventListener('keyup', (e) => {
        e.stopPropagation();
    });

    input.addEventListener('paste', (e) => {
        e.stopPropagation();
    });

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.innerText = 'Send';
    submitBtn.style.cssText = `
        padding: 6px 12px;
        border: 1px solid #007cba;
        background: #007cba;
        color: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
    `;

    // Add styles for disabled state
    const updateSubmitButtonStyle = () => {
        if (submitBtn.disabled) {
            submitBtn.style.background = '#95a5a6';
            submitBtn.style.borderColor = '#95a5a6';
            submitBtn.style.cursor = 'not-allowed';
        } else {
            submitBtn.style.background = '#007cba';
            submitBtn.style.borderColor = '#007cba';
            submitBtn.style.cursor = 'pointer';
        }
    };

    // Watch for disabled attribute changes
    const observer = new MutationObserver(updateSubmitButtonStyle);
    observer.observe(submitBtn, { attributes: true, attributeFilter: ['disabled'] });

    dialog.appendChild(submitBtn);

    // Show/hide dialog
    let documentClickListener: ((e: Event) => void) | null = null;
    let resizeListener: (() => void) | null = null;

    const closeDialog = () => {
        dialog.style.display = 'none';
        if (documentClickListener) {
            document.removeEventListener('mousedown', documentClickListener);
            documentClickListener = null;
        }
        if (resizeListener) {
            window.removeEventListener('resize', resizeListener);
            resizeListener = null;
        }
    };
    const openDialog = () => {
        // Append dialog to body for better positioning
        if (!dialog.parentElement) {
            document.body.appendChild(dialog);
        }

        dialog.style.display = 'block';
        positionDialog(); // Position the dialog when opening

        // Focus the input after a small delay to ensure it's visible
        setTimeout(() => {
            input.focus();
        }, 50);

        // Set up click outside listener
        setTimeout(() => {
            documentClickListener = (e: Event) => {
                const target = e.target as Element;
                if (!container.contains(target) && !dialog.contains(target)) {
                    closeDialog();
                }
            };
            document.addEventListener('mousedown', documentClickListener);
        }, 100);

        // Set up resize listener to reposition dialog
        resizeListener = () => {
            if (dialog.style.display === 'block') {
                positionDialog();
            }
        };
        window.addEventListener('resize', resizeListener);
    };
    button.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (dialog.style.display === 'block') {
            closeDialog();
        } else {
            openDialog();
        }
    });

    // Submit handler
    submitBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!input.value.trim()) {
            messageArea.textContent = 'Please enter instructions for AI.';
            messageArea.style.color = '#d63031';
            return;
        }

        // Disable submit button and show loading state
        submitBtn.disabled = true;
        submitBtn.innerText = 'Generating...';
        messageArea.textContent = 'Generating content...';
        messageArea.style.color = '#0984e3';

        if (!contentRef.current) return;
        const editorHtml = contentRef.current.innerHTML;
        const userPrompt = input.value;
        try {
            // create a random session id in form of a valid GUID
            const sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = (Math.random() * 16) | 0;
                const v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });

            const simplifiedData = simplifyData(data || {}, 30);

            const response = await fetch(`${apiEndpoint}/${sessionId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authorizationToken
                        ? { Authorization: `Bearer ${authorizationToken}` }
                        : {}),
                },
                body: JSON.stringify({
                    messages: [
                        {
                            content: `user instructions:\n${userPrompt}\n${'-'.repeat(40)}\nCurrent HTML Content:\n${editorHtml}\n${'-'.repeat(40)}\nCurrent Data:\n${data ? JSON.stringify(simplifiedData) : ''}`,
                        },
                    ],
                    requestData: {
                        agent: 'test-agent-01',
                        streamResponse: false,
                    },
                }),
            });
            const result: {
                response_data?: string;
                response_text?: string;
            } = await response.json();

            if (result.response_data) {
                contentRef.current.innerHTML = result.response_data;
                messageArea.textContent = 'Content generated successfully!';
                messageArea.style.color = '#00b894';
                // Clear input after successful generation
                input.value = '';
            } else if (result.response_text) {
                messageArea.textContent = result.response_text;
                messageArea.style.color = '#636e72';
            } else {
                messageArea.textContent = 'No content generated.';
                messageArea.style.color = '#636e72';
            }
        } catch (err) {
            let errorMsg = 'Failed to generate content.';
            if (err && typeof err === 'object' && 'message' in err) {
                errorMsg = (err as any).message;
            }
            messageArea.textContent = 'Error: ' + errorMsg;
            messageArea.style.color = '#d63031';
        } finally {
            // Re-enable submit button
            submitBtn.disabled = false;
            submitBtn.innerText = 'Send';
        }
    });

    // Cleanup
    (container as any)._cleanup = () => {
        closeDialog();
        // Remove dialog from body if it was added there
        if (dialog.parentElement === document.body) {
            document.body.removeChild(dialog);
        }
    };

    container.appendChild(button);
    // Don't append dialog to container - it will be added to body when opened
    return container;
};
